<template>
  <draggable
    v-bind="dragOptions"
    :list="list"
    :class="[
      'list',
      parent?.type === 'category' && 'list--category',
      parent?.type === 'tournament' && 'list--tournament',
    ]"
  >
    <li
      v-for="(item, index) in list"
      :key="item.id"
      :ref="'listItem-' + item.id"
    >
      <template v-if="item.type === 'category'">
        <DynamicListItem
          :index="index"
          :isFirst="index === 0"
          :isLast="index === list.length - 1"
          :hasDelete="false"
          :noContent="!item.children?.length"
          theme="white"
          size="big"
          @moveUp="() => handleMoveUp(index)"
          @moveDown="() => handleMoveDown(index)"
          @moveTop="() => handleMoveTop(index)"
          @moveBottom="() => handleMoveBottom(index)"
          @remove="() => handleRemove(index)"
        >
          <template #title>
            <div class="category__header">
              <img
                v-if="item.selectedIcon"
                :src="item.selectedIcon.colors.icon_path"
                :alt="item.selectedIcon.name"
                :class="['category__img', !showAllCategoriesTitle && 'muted']"
              />
              <div class="category__title">
                <span :class="[!showAllCategoriesTitle && 'muted']">
                  {{ item.title }}
                </span>
                <a-button type="link" @click="$emit('toggleCategoryTitle')">
                  <a-icon v-if="showAllCategoriesTitle" type="eye" />
                  <a-icon v-else type="eye-invisible" />
                </a-button>
              </div>
            </div>
          </template>
          <template #actions>
            <a-button
              type="primary"
              size="large"
              class="category__match"
              @click="$emit('openMatchesModal', item.id)"
            >
              <a-icon type="plus" />
              <span>Match</span>
            </a-button>
            <a-button
              type="link"
              class="category__edit"
              @click="$emit('editCategory', item.id)"
            >
              <a-icon type="edit" />
            </a-button>
          </template>
          <ContentNestedList
            v-if="item.children"
            :list="item.children"
            :selectedLanguage="selectedLanguage"
            :parent="item"
            @editWidget="(data) => $emit('editWidget', data)"
            @deleteWidget="(data) => $emit('deleteWidget', data)"
          />
        </DynamicListItem>
      </template>

      <template v-else-if="item.type === 'tournament'">
        <DynamicListItem
          :index="index"
          :isFirst="index === 0"
          :isLast="index === list.length - 1"
          :noContent="!item.children?.length"
          theme="white"
          size="big"
          @moveUp="() => handleMoveUp(index)"
          @moveDown="() => handleMoveDown(index)"
          @moveTop="() => handleMoveTop(index)"
          @moveBottom="() => handleMoveBottom(index)"
          @remove="() => handleRemove(index)"
        >
          <template #title>
            <div class="tournament__title">
              <span :class="[!parent?.showTournaments && 'muted']">
                {{ item.name }}
              </span>
              <a-button
                type="link"
                @click="() => toggleTournamentTitle(parent)"
              >
                <a-icon v-if="parent.showTournaments" type="eye" />
                <a-icon v-else type="eye-invisible" />
              </a-button>
            </div>
          </template>
          <ContentNestedList
            v-if="item.children"
            :list="item.children"
            :selectedLanguage="selectedLanguage"
            :parent="item"
            @editWidget="(data) => $emit('editWidget', data)"
            @deleteWidget="(data) => $emit('deleteWidget', data)"
          />
        </DynamicListItem>
      </template>

      <template v-else-if="item.type === 'match'">
        <DynamicListItem
          :index="index"
          :isFirst="index === 0"
          :isLast="index === list.length - 1"
          theme="gray"
          @moveUp="() => handleMoveUp(index)"
          @moveDown="() => handleMoveDown(index)"
          @moveTop="() => handleMoveTop(index)"
          @moveBottom="() => handleMoveBottom(index)"
          @remove="() => handleRemove(index)"
        >
          <template #title>
            <div class="match__header">
              <span v-if="item.date" class="match__date">
                {{ $moment(item.date).format("DD.MM.YYYY") }}
              </span>
              <span class="match__name">{{ item.name }}</span>
            </div>
          </template>
        </DynamicListItem>
      </template>

      <template v-else-if="item.type === 'widget'">
        <DynamicListItem
          :has-controls="false"
          :index="index"
          :isFirst="index === 0"
          :isLast="index === list.length - 1"
          theme="gray"
          draggable
          @remove="() => handleRemove(index)"
        >
          <template #title>
            <div class="widget">
              <div class="name">{{ item.name }}</div>
              <img :src="item.backgroundImageUrl" />
            </div>
          </template>
          <template #actions>
            <a-button
              type="link"
              class="category__edit"
              @click="$emit('editWidget', item.id)"
            >
              <a-icon type="edit" />
            </a-button>
          </template>
        </DynamicListItem>
      </template>
    </li>
  </draggable>
</template>

<script>
import draggable from 'vuedraggable'
import DynamicListItem from '@/components/custom/list/dynamicListItem.vue'

import { mapGetters } from 'vuex'

export default {
  components: {
    draggable,
    DynamicListItem,
  },
  name: 'ContentNestedList',
  props: {
    list: {
      type: Array,
      required: true,
    },
    selectedLanguage: {
      type: String,
      required: true,
    },
    parent: {
      type: Object,
    },
  },
  data() {
    return {
      dragOptions: {
        emptyInsertThreshold: 100,
        animation: 300,
        group: 'g1',
        tag: 'ul',
        handle: '.drag-handle',
      },
      isScrolling: false,
      scrollDirection: 0,
      scrollSpeed: 5, // pixels per frame
      topBoundary: 170, // px from top
      bottomBoundary: 20, // px from bottom
    }
  },
  computed: {
    ...mapGetters(['showAllCategoriesTitle']),
  },
  methods: {
    toggleTournamentTitle(item) {
      if (item) {
        item.showTournaments = !item.showTournaments
      }
    },
    animateSwap(fromIndex, toIndex) {
      const fromItem = this.list[fromIndex]
      const toItem = this.list[toIndex]
      if (!fromItem || !toItem) return

      const fromEl = this.$refs['listItem-' + fromItem.id]?.[0]
      const toEl = this.$refs['listItem-' + toItem.id]?.[0]
      if (!fromEl || !toEl) return

      // Запоминанем положение до изменений
      const fromRect = fromEl.getBoundingClientRect()
      const toRect = toEl.getBoundingClientRect()

      // Меняем данные в массиве
      this.$set(this.list, fromIndex, toItem)
      this.$set(this.list, toIndex, fromItem)

      this.$nextTick(() => {
        // Запоминанием новое положение после обновления DOM
        const fromNewRect = fromEl.getBoundingClientRect()
        const toNewRect = toEl.getBoundingClientRect()

        // Рассчитываем дельту
        const fromDeltaY = fromRect.top - fromNewRect.top
        const toDeltaY = toRect.top - toNewRect.top

        // Применяем моментальную инверсию - возвращаем элементы на первоначальное положение
        fromEl.style.transition = 'none'
        toEl.style.transition = 'none'
        fromEl.style.transform = `translateY(${fromDeltaY}px)`
        toEl.style.transform = `translateY(${toDeltaY}px)`

        // Обращаемся к offsetHeight, чтобы заставить браузер пересчитать его
        // eslint-disable-next-line no-void
        void fromEl.offsetHeight
        // eslint-disable-next-line no-void
        void toEl.offsetHeight

        // Анимируем элементы к конечному положению
        fromEl.style.transition = 'transform 0.3s ease'
        toEl.style.transition = 'transform 0.3s ease'
        fromEl.style.transform = 'translateY(0)'
        toEl.style.transform = 'translateY(0)'

        // Убираем стили после завершения анмиации
        const onTransitionEnd = (el) => {
          el.style.transition = ''
          el.style.transform = ''
          el.removeEventListener('transitionend', onTransitionEnd)
        }
        fromEl.addEventListener('transitionend', () => onTransitionEnd(fromEl))
        toEl.addEventListener('transitionend', () => onTransitionEnd(toEl))
      })
    },
    handleMoveUp(index) {
      if (index > 0) this.animateSwap(index, index - 1)
    },
    handleMoveDown(index) {
      if (index < this.list.length - 1) this.animateSwap(index, index + 1)
    },
    handleMoveTop(index) {
      for (let i = index; i > 0; i--) {
        this.animateSwap(i, i - 1)
      }
    },
    handleMoveBottom(index) {
      for (let i = index; i < this.list.length - 1; i++) {
        this.animateSwap(i, i + 1)
      }
    },
    handleRemove(index) {
      const item = this.list[index]

      const el = this.$refs['listItem-' + item.id]?.[0]
      const isMatch = item.type === 'match'
      const isChamp = item.type === 'tournament'

      const finishRemove = () => {
        this.list.splice(index, 1)

        if (isMatch) {
          this.$store.dispatch('content/removeChampFromBlocks', item)
        }

        if (isMatch || isChamp) {
          this.$store.dispatch('filter/removeMatchFromFilters', item)
        }
      }

      if (!el) {
        finishRemove()
        return
      }

      el.style.height = el.offsetHeight + 'px'
      el.style.marginTop = getComputedStyle(el).marginTop
      el.style.opacity = '1'
      el.style.transform = 'translateX(0)'
      el.style.transition = 'none'

      requestAnimationFrame(() => {
        const onFadeOutEnd = (event) => {
          if (event.propertyName === 'opacity') {
            el.removeEventListener('transitionend', onFadeOutEnd)

            // Начинаем анимацию схлопывания высоты
            el.style.transition = 'height 0.3s ease, margin-top 0.3s ease'
            el.style.height = '0'
            el.style.marginTop = '0'

            const onCollapseEnd = (event) => {
              if (event.propertyName === 'height') {
                el.removeEventListener('transitionend', onCollapseEnd)
                finishRemove()
              }
            }
            el.addEventListener('transitionend', onCollapseEnd)
          }
        }

        el.style.transition = 'opacity 0.3s ease, transform 0.3s ease'
        el.style.opacity = '0'
        el.style.transform = 'translateX(20px)'
        el.addEventListener('transitionend', onFadeOutEnd)
      })
    },
    handleAutoscroll(e) {
      if (e.clientY < this.topBoundary) {
        this.startScroll(-1) // scroll up
      } else if (e.clientY > window.innerHeight - this.bottomBoundary) {
        this.startScroll(1) // scroll down
      } else {
        this.stopScroll()
      }
    },
    startScroll(direction) {
      if (this.isScrolling && this.scrollDirection === direction) return

      this.scrollDirection = direction
      if (!this.isScrolling) {
        this.isScrolling = true
        this.scrollStep()
      }
    },
    stopScroll() {
      this.isScrolling = false
      this.scrollDirection = 0
    },
    scrollStep() {
      if (!this.isScrolling) return

      window.scrollBy(0, this.scrollSpeed * this.scrollDirection)
      requestAnimationFrame(this.scrollStep)
    },
  },
  mounted() {
    window.addEventListener('dragover', this.handleAutoscroll)
  },
  beforeDestroy() {
    window.removeEventListener('dragover', this.handleAutoscroll)
    this.stopScroll()
  },
}
</script>
<style scoped lang="scss">
.muted {
  opacity: 0.5 !important;
}

.list {
  list-style: none;
  padding: 0;
  margin: 0;

  &--category {
    gap: 20px;

    & ::v-deep .item__content {
      margin-top: 12px;
    }
  }

  &--tournament {
    gap: 12px;
  }

  li {
    will-change: transform;
    transition: all 0.3s ease;

    &:not(:first-child) {
      margin-top: 20px;
    }
  }
}

.category,
.tournament {
  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: #000000;

    &::v-deep .ant-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: none;
      height: auto;
      width: 20px;
      height: 20px;
    }

    &::v-deep .anticon {
      font-size: 17px;
      color: #8c8c8c;

      &:hover,
      &:focus {
        color: #8c8c8c;
      }
    }
  }

  &__img {
    width: 24px;
    height: 24px;
    filter: brightness(0);
  }

  &__match {
    display: flex;
    align-items: center;
    // gap: 2px;
    font-size: 14px;
    line-height: 22px;
    width: auto !important;
    height: 24px !important;
    padding: 1px 8px !important;

    span {
      margin-left: 0;
    }
  }

  &__edit {
    font-size: 19px;
  }
}

.tournament {
  &__title {
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    color: #000000;
  }
}

.match {
  &__header {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__date {
    font-size: 14px;
    line-height: 22px;
    color: #8c8c8c;
  }

  &__name {
    font-size: 16px;
    line-height: 24px;
    color: #000000;
  }
}

.widget {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;

  .name {
    font-size: 16px;
    line-height: 24px;
    color: #000000;
  }

  img {
    max-height: 60px;
    max-width: 100%;
  }
}
</style>
